import { useEffect, useState } from "react";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, Tooltip, CartesianGrid, Cell,
} from "recharts";
import {
  Users, UserCheck, Euro, TrendingUp, MessageSquare, FolderOpen,
  Volume2, Loader2, ShieldAlert, Ban, CheckCircle2, Trash2, Search, KeyRound, X, BadgeCheck, ChevronDown, Check,
} from "lucide-react";
import { Navigate } from "react-router-dom";
import { AppShell } from "../components/AppShell";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "../components/ui/dropdown-menu";
import { useAuth } from "../context/AuthContext";
import { StripeSubscriptions } from "../components/StripeSubscriptions";
import { StripeKeysPanel } from "../components/StripeKeysPanel";
import { LlmModelPanel } from "../components/LlmModelPanel";
import { api, apiError } from "../lib/apiClient";
import { toast } from "sonner";

const ACCENT = "var(--accent)";
const PLAN_COLORS = { free: "#7280a0", pro: "var(--accent)", ultra: "#c084fc" };
const PLAN_OPTIONS = [
  { id: "free", label: "Découverte (Gratuit)" },
  { id: "pro", label: "Pro" },
  { id: "ultra", label: "Entreprise" },
];

const fmtDay = (d) => {
  const p = (d || "").split("-");
  return p.length === 3 ? `${p[2]}/${p[1]}` : d;
};
const fmtDate = (iso) => {
  if (!iso) return "—";
  const p = iso.slice(0, 10).split("-");
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : iso;
};
const fmtEuro = (n) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);
const fmtNum = (n) => (n || 0).toLocaleString("fr-FR");

const tooltipStyle = {
  background: "rgba(3,10,22,0.95)",
  border: "1px solid var(--accent)",
  borderRadius: 6,
  fontFamily: "Share Tech Mono, monospace",
  fontSize: 12,
  color: "#eaf4ff",
};

function KpiCard({ icon: Icon, label, value, sub, testid, tone = "accent" }) {
  const toneColor = tone === "revenue" ? "#c084fc" : tone === "active" ? "#00ffaa" : "var(--accent)";
  return (
    <div data-testid={testid} className="hud-panel reticle clip-corner p-4 sm:p-5">
      <span className="rc-tr" />
      <span className="rc-bl" />
      <div className="flex items-center gap-2 text-[#8b949e]">
        <Icon className="h-4 w-4" style={{ color: toneColor }} />
        <span className="font-mono-hud text-[9px] tracking-[0.2em] uppercase">{label}</span>
      </div>
      <div
        className="mt-2 font-display text-2xl sm:text-3xl font-black tabular-nums text-glow"
        style={{ color: toneColor }}
      >
        {value}
      </div>
      {sub && (
        <div className="mt-1 font-mono-hud text-[10px] tracking-[0.15em] text-[#6b7a8d] uppercase">
          {sub}
        </div>
      )}
    </div>
  );
}

function Panel({ title, children, className = "" }) {
  return (
    <div className={`hud-panel clip-corner p-4 sm:p-5 ${className}`}>
      <h3 className="font-mono-hud text-[10px] tracking-[0.25em] text-[#8b949e] uppercase mb-4">
        {title}
      </h3>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [pwdInfo, setPwdInfo] = useState(null);

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    Promise.all([api.get("/admin/overview"), api.get("/admin/users?limit=100")])
      .then(([ov, u]) => {
        setOverview(ov.data);
        setUsers(u.data.users || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  // guard: only admin
  if (user && user.role !== "admin") {
    return <Navigate to="/" replace />;
  }

  const currentId = user?.id;

  const blockUser = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/block`);
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, blocked: true } : x)));
      toast.success(`${u.email} bloqué — connexion désormais refusée`);
    } catch (e) {
      toast.error("Échec du blocage");
    }
  };
  const activateUser = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/activate`);
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, blocked: false } : x)));
      toast.success(`${u.email} réactivé`);
    } catch (e) {
      toast.error("Échec de l'activation");
    }
  };
  const approveUser = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/approve`);
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, approved: true } : x)));
      toast.success(`${u.email} approuvé — il peut désormais se connecter`);
    } catch (e) {
      toast.error("Échec de l'approbation");
    }
  };
  const setUserPlan = async (u, plan) => {
    if (u.plan === plan) return;
    try {
      const { data } = await api.post(`/admin/users/${u.id}/plan`, { plan });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, plan, plan_name: data.plan_name } : x)));
      toast.success(`${u.email} → ${data.plan_name} (géré par l'admin, sans paiement)`);
    } catch (e) {
      toast.error("Échec du changement d'offre");
    }
  };
  const deleteUser = async (u) => {
    if (!window.confirm(`Supprimer définitivement ${u.email} et toutes ses données ? Cette action est irréversible.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
      toast.success(`${u.email} supprimé`);
    } catch (e) {
      toast.error("Échec de la suppression");
    }
  };
  const resetPassword = async (u) => {
    const custom = window.prompt(
      `Nouveau mot de passe pour ${u.email}\n(laisse vide pour en générer un automatiquement — 6 caractères minimum)`,
      ""
    );
    if (custom === null) return;
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-password`, {
        new_password: custom.trim() || null,
      });
      setPwdInfo({ email: data.email, password: data.password, generated: data.generated });
      toast.success(`Mot de passe réinitialisé pour ${data.email}`);
    } catch (e) {
      toast.error(apiError(e, "Réinitialisation impossible"));
    }
  };

  const totals = overview?.totals || {};
  const revenue = overview?.revenue || { mrr_eur: 0, arr_eur: 0, plan_counts: [] };
  const series = overview?.series || { signups: [], messages: [], voice: [], documents: [] };

  const q = search.trim().toLowerCase();
  const filteredUsers = users.filter((u) => {
    if (q && !((u.email || "").toLowerCase().includes(q) || (u.name || "").toLowerCase().includes(q))) return false;
    if (filter === "pending") return u.approved === false;
    if (filter === "blocked") return u.blocked;
    if (filter === "active") return !u.blocked && u.approved !== false;
    if (filter === "paid") return u.plan && u.plan !== "free";
    if (filter === "free") return !u.plan || u.plan === "free";
    return true;
  });

  return (
    <AppShell title="Administration" subtitle="Vue globale de la plateforme Sara Connor">
      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-6 w-6 animate-spin accent-text" />
        </div>
      ) : (
        <div className="space-y-6" data-testid="admin-content">
          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <KpiCard
              testid="kpi-users"
              icon={Users}
              label="Utilisateurs"
              value={fmtNum(totals.users)}
              sub={`+${totals.new_users_month || 0} ce mois-ci`}
            />
            <KpiCard
              testid="kpi-active"
              icon={UserCheck}
              label="Actifs 30 j"
              value={fmtNum(totals.active_users_30d)}
              sub="au moins 1 message"
              tone="active"
            />
            <KpiCard
              testid="kpi-mrr"
              icon={Euro}
              label="MRR"
              value={fmtEuro(revenue.mrr_eur)}
              sub={`ARR ${fmtEuro(revenue.arr_eur)}`}
              tone="revenue"
            />
            <KpiCard
              testid="kpi-messages"
              icon={MessageSquare}
              label="Messages IA"
              value={fmtNum(totals.messages)}
              sub={`${fmtNum(totals.voice_chars)} car. voix`}
            />
          </div>

          {/* Secondary KPIs row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <KpiCard testid="kpi-docs" icon={FolderOpen} label="Documents indexés" value={fmtNum(totals.documents)} />
            <KpiCard testid="kpi-mems" icon={TrendingUp} label="Souvenirs stockés" value={fmtNum(totals.memories)} />
            <KpiCard
              testid="kpi-voice"
              icon={Volume2}
              label="Caractères vocalisés"
              value={fmtNum(totals.voice_chars)}
              tone="active"
            />
            <KpiCard testid="kpi-pro" icon={ShieldAlert} label="Abonnés payants"
              value={fmtNum(revenue.plan_counts.filter(p => p.plan !== "free").reduce((s, p) => s + p.count, 0))}
              sub="Pro + Ultra"
              tone="revenue"
            />
          </div>

          {/* Stripe & Subscriptions */}
          <StripeSubscriptions />

          {/* Stripe keys — editable by the owner */}
          <StripeKeysPanel />

          {/* AI model selector — Studio & e-mails */}
          <LlmModelPanel />

          {/* Charts row 1 */}
          <div className="grid lg:grid-cols-2 gap-4 sm:gap-6">
            <Panel title="Inscriptions (30 j)">
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={series.signups} margin={{ top: 5, right: 8, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gSignups" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={ACCENT} stopOpacity={0.5} />
                        <stop offset="100%" stopColor={ACCENT} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="date" tickFormatter={fmtDay} tick={{ fill: "#6b7a8d", fontSize: 10, fontFamily: "Share Tech Mono" }} />
                    <YAxis allowDecimals={false} tick={{ fill: "#6b7a8d", fontSize: 10, fontFamily: "Share Tech Mono" }} />
                    <Tooltip contentStyle={tooltipStyle} labelFormatter={fmtDay} />
                    <Area type="monotone" dataKey="count" name="Nouveaux comptes" stroke={ACCENT} strokeWidth={2} fill="url(#gSignups)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </Panel>

            <Panel title="Messages IA (30 j)">
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={series.messages} margin={{ top: 5, right: 8, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gMsgs" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#00ffaa" stopOpacity={0.5} />
                        <stop offset="100%" stopColor="#00ffaa" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="date" tickFormatter={fmtDay} tick={{ fill: "#6b7a8d", fontSize: 10, fontFamily: "Share Tech Mono" }} />
                    <YAxis allowDecimals={false} tick={{ fill: "#6b7a8d", fontSize: 10, fontFamily: "Share Tech Mono" }} />
                    <Tooltip contentStyle={tooltipStyle} labelFormatter={fmtDay} />
                    <Area type="monotone" dataKey="count" name="Messages" stroke="#00ffaa" strokeWidth={2} fill="url(#gMsgs)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </Panel>
          </div>

          {/* Charts row 2 */}
          <div className="grid lg:grid-cols-2 gap-4 sm:gap-6">
            <Panel title="Consommation vocale (car. / 30 j)">
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={series.voice} margin={{ top: 5, right: 8, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="date" tickFormatter={fmtDay} tick={{ fill: "#6b7a8d", fontSize: 10, fontFamily: "Share Tech Mono" }} />
                    <YAxis tick={{ fill: "#6b7a8d", fontSize: 10, fontFamily: "Share Tech Mono" }} />
                    <Tooltip contentStyle={tooltipStyle} labelFormatter={fmtDay} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                    <Bar dataKey="chars" name="Caractères" fill="#00ffaa" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Panel>

            <Panel title="Répartition abonnements">
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={revenue.plan_counts} margin={{ top: 5, right: 8, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="name" tick={{ fill: "#6b7a8d", fontSize: 10, fontFamily: "Share Tech Mono" }} />
                    <YAxis allowDecimals={false} tick={{ fill: "#6b7a8d", fontSize: 10, fontFamily: "Share Tech Mono" }} />
                    <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(255,255,255,0.04)" }}
                      formatter={(v, k, p) => k === "revenue" ? fmtEuro(v) : v} />
                    <Bar dataKey="count" name="Comptes" radius={[3, 3, 0, 0]}>
                      {revenue.plan_counts.map((p) => (
                        <Cell key={p.plan} fill={PLAN_COLORS[p.plan] || ACCENT} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Panel>
          </div>

          {/* Users table */}
          <Panel title={`Utilisateurs (${filteredUsers.length}/${users.length})`}>
            {pwdInfo && (
              <div
                data-testid="admin-password-result"
                className="mb-4 flex flex-wrap items-center gap-3 clip-corner border border-[var(--accent)] bg-[var(--accent-a10)] p-3.5"
              >
                <KeyRound className="h-4 w-4 accent-text" />
                <span className="font-mono-hud text-[11px] text-[#e1e9f6]">
                  {pwdInfo.email} —{" "}
                  {pwdInfo.generated ? "nouveau mot de passe généré :" : "nouveau mot de passe :"}
                </span>
                <code data-testid="admin-password-value" className="font-mono text-sm text-white">
                  {pwdInfo.password}
                </code>
                <button
                  type="button"
                  data-testid="admin-password-copy"
                  onClick={() => navigator.clipboard?.writeText(pwdInfo.password)}
                  className="clip-corner border border-[var(--border-hud)] px-2.5 py-1 font-mono-hud text-[10px] uppercase tracking-widest text-[#8b949e] transition-colors hover:text-white"
                >
                  Copier
                </button>
                <button
                  type="button"
                  data-testid="admin-password-dismiss"
                  onClick={() => setPwdInfo(null)}
                  className="ml-auto text-[#6b7a8d] transition-colors hover:text-white"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="relative w-full sm:max-w-xs">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#6b7a8d]" />
                <input
                  data-testid="admin-user-search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechercher par nom ou e-mail…"
                  className="w-full clip-corner border border-[var(--border-hud)] bg-[rgba(2,8,18,0.8)] py-2 pl-9 pr-3 text-sm text-white outline-none transition-colors placeholder:text-[#3a4a5a] focus:border-[var(--accent)] focus:shadow-[var(--accent-glow)]"
                />
              </div>
              <div className="flex flex-wrap gap-1.5">
                {[
                  { id: "all", label: "Tous" },
                  { id: "pending", label: "En attente" },
                  { id: "active", label: "Actifs" },
                  { id: "blocked", label: "Bloqués" },
                  { id: "paid", label: "Payants" },
                  { id: "free", label: "Gratuits" },
                ].map((f) => (
                  <button
                    key={f.id}
                    data-testid={`admin-filter-${f.id}`}
                    onClick={() => setFilter(f.id)}
                    className={`clip-corner border px-3 py-1.5 font-mono-hud text-[10px] uppercase tracking-widest transition-colors ${
                      filter === f.id
                        ? "border-[var(--accent)] bg-[var(--accent-a15)] text-white"
                        : "border-[var(--border-hud)] text-[#8b949e] hover:text-white"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="overflow-x-auto no-scrollbar -mx-4 sm:mx-0">
              <table className="w-full text-sm min-w-[1040px]" data-testid="admin-users-table">
                <thead>
                  <tr className="font-mono-hud text-[10px] tracking-[0.2em] text-[#6b7a8d] uppercase border-b border-[var(--border-hud)]">
                    <th className="text-left py-2 px-3">Compte</th>
                    <th className="text-left py-2 px-3">Plan</th>
                    <th className="text-left py-2 px-3">Statut</th>
                    <th className="text-left py-2 px-3">Abonnement</th>
                    <th className="text-right py-2 px-3">Msgs</th>
                    <th className="text-right py-2 px-3">Docs</th>
                    <th className="text-right py-2 px-3">Voix (car.)</th>
                    <th className="text-right py-2 px-3">Inscrit</th>
                    <th className="text-right py-2 px-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((u) => (
                    <tr
                      key={u.id}
                      className="border-b border-[rgba(255,255,255,0.04)] hover:bg-[var(--accent-a10)] transition-colors"
                      data-testid={`user-row-${u.id}`}
                    >
                      <td className="py-2.5 px-3">
                        <div className="text-white font-medium truncate max-w-[220px]">{u.name || "—"}</div>
                        <div className="text-[11px] text-[#6b7a8d] font-mono-hud truncate max-w-[220px]">{u.email}</div>
                      </td>
                      <td className="py-2.5 px-3">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              data-testid={`user-plan-${u.id}`}
                              title="Changer l'offre (géré par l'admin, sans paiement)"
                              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full font-mono-hud text-[10px] tracking-widest uppercase border cursor-pointer hover:opacity-80 transition-opacity"
                              style={{
                                color: PLAN_COLORS[u.plan] || "#8b949e",
                                borderColor: PLAN_COLORS[u.plan] || "#8b949e",
                              }}
                            >
                              {u.plan_name} <ChevronDown className="h-3 w-3" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start" className="min-w-[170px]">
                            {PLAN_OPTIONS.map((opt) => (
                              <DropdownMenuItem
                                key={opt.id}
                                data-testid={`user-plan-${u.id}-${opt.id}`}
                                onClick={() => setUserPlan(u, opt.id)}
                                className="cursor-pointer text-xs"
                              >
                                <span className="h-2 w-2 rounded-full mr-2" style={{ background: PLAN_COLORS[opt.id] }} />
                                {opt.label}
                                {u.plan === opt.id && <Check className="h-3.5 w-3.5 ml-auto accent-text" />}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                        {u.role === "admin" && (
                          <span className="ml-2 text-[9px] font-mono-hud tracking-widest text-yellow-400">ADMIN</span>
                        )}
                      </td>
                      <td className="py-2.5 px-3">
                        {u.blocked ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-mono-hud text-[10px] tracking-widest uppercase border border-[#ff6b6b] text-[#ff6b6b]">Bloqué</span>
                        ) : u.approved === false ? (
                          <span data-testid={`user-pending-${u.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-mono-hud text-[10px] tracking-widest uppercase border border-[#ffab00] text-[#ffab00]">En attente</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-mono-hud text-[10px] tracking-widest uppercase border border-[#00ffaa] text-[#00ffaa]">Actif</span>
                        )}
                      </td>
                      <td className="py-2.5 px-3 text-[11px] font-mono-hud text-[#e1e9f6]">
                        {u.subscription_ends_at ? fmtDate(u.subscription_ends_at) : "—"}
                      </td>
                      <td className="py-2.5 px-3 text-right tabular-nums text-[#e1e9f6]">{fmtNum(u.messages)}</td>
                      <td className="py-2.5 px-3 text-right tabular-nums text-[#e1e9f6]">{fmtNum(u.documents)}</td>
                      <td className="py-2.5 px-3 text-right tabular-nums text-[#e1e9f6]">{fmtNum(u.voice_chars)}</td>
                      <td className="py-2.5 px-3 text-right tabular-nums text-[10px] text-[#8b949e] font-mono-hud">
                        {(u.created_at || "").slice(0, 10)}
                      </td>
                      <td className="py-2.5 px-3">
                        <div className="flex items-center justify-end gap-1.5">
                          {u.approved === false && (
                            <button
                              data-testid={`user-approve-${u.id}`}
                              onClick={() => approveUser(u)}
                              title="Valider le compte pour autoriser la connexion"
                              className="flex items-center gap-1 px-2 py-1 clip-corner border border-[var(--accent)]/60 accent-text text-[10px] font-mono-hud uppercase tracking-widest hover:bg-[var(--accent-a10)] transition-colors"
                            >
                              <BadgeCheck className="h-3.5 w-3.5" /> Approuver
                            </button>
                          )}
                          {u.blocked ? (
                            <button
                              data-testid={`user-activate-${u.id}`}
                              onClick={() => activateUser(u)}
                              title="Activer le compte"
                              className="flex items-center gap-1 px-2 py-1 clip-corner border border-[#00ffaa]/50 text-[#00ffaa] text-[10px] font-mono-hud uppercase tracking-widest hover:bg-[#00ffaa]/10 transition-colors"
                            >
                              <CheckCircle2 className="h-3.5 w-3.5" /> Activer
                            </button>
                          ) : (
                            <button
                              data-testid={`user-block-${u.id}`}
                              onClick={() => blockUser(u)}
                              disabled={u.id === currentId}
                              title={u.id === currentId ? "Vous ne pouvez pas vous bloquer" : "Bloquer le compte"}
                              className="flex items-center gap-1 px-2 py-1 clip-corner border border-[#ffab00]/50 text-[#ffab00] text-[10px] font-mono-hud uppercase tracking-widest hover:bg-[#ffab00]/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                              <Ban className="h-3.5 w-3.5" /> Bloquer
                            </button>
                          )}
                          <button
                            data-testid={`user-reset-password-${u.id}`}
                            onClick={() => resetPassword(u)}
                            title="Réinitialiser le mot de passe"
                            className="flex items-center gap-1 px-2 py-1 clip-corner border border-[var(--accent)]/50 accent-text text-[10px] font-mono-hud uppercase tracking-widest hover:bg-[var(--accent-a10)] transition-colors"
                          >
                            <KeyRound className="h-3.5 w-3.5" /> Mot de passe
                          </button>
                          <button
                            data-testid={`user-delete-${u.id}`}
                            onClick={() => deleteUser(u)}
                            disabled={u.id === currentId}
                            title={u.id === currentId ? "Vous ne pouvez pas vous supprimer" : "Supprimer le compte"}
                            className="flex items-center gap-1 px-2 py-1 clip-corner border border-[#ff6b6b]/50 text-[#ff6b6b] text-[10px] font-mono-hud uppercase tracking-widest hover:bg-[#ff6b6b]/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <Trash2 className="h-3.5 w-3.5" /> Suppr.
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredUsers.length === 0 && (
                    <tr>
                      <td colSpan={9} className="py-8 text-center text-[#6b7a8d] font-mono-hud text-xs uppercase tracking-widest">
                        {users.length === 0 ? "Aucun utilisateur pour le moment" : "Aucun résultat pour ces critères"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Panel>
        </div>
      )}
    </AppShell>
  );
}
